import { useCallback, useEffect, useState } from 'react';
import useConfig from '../../../hooks/useConfig';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import {
  fetchDetailedDailyCategories,
  fetchDetailedDailyDowntime,
  fetchDetailedDailyDowntimeTier1,
  selectDetailedDailyDowntime,
  selectDetailedDailyDowntimeLoading,
} from '../DetailedDailySlice';
import Loader from '../../common/Loader/Loader';
import shutdownIcon from '../../../images/shutdown.svg';

import './DetailedDaily.scss';
import Modal from '../../common/Modal/Modal';
import { deleteDetailedDailyDowntime } from '../../../services/downtime/DetailedDailyServices';
import AddEditDowntime from './AddEditDowntime';
import { DetailedDailyDowntimeItem } from './DetailedDailyTypes';
import DetailedDailyTable from './DetailedDailyTable';
import { useParams } from 'react-router-dom';
import { fetchColumnsData } from '../../../dispatchers/ColumnSelectorDispatcher';

const DetailedDaily: React.FC = () => {
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [addEditOpen, setAddEditOpen] = useState<boolean>(false);
  const { enablePumpChecker } = useConfig();
  const { assetId } = useParams<{ assetId: string }>();
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);

  const dispatch = useAppDispatch();
  const wellId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);
  const data = useAppSelector(selectDetailedDailyDowntime) || [];
  const dataLoading = useAppSelector(selectDetailedDailyDowntimeLoading);
  const industryApplicationId = useAppSelector((state: any) => state.AssetAnalysis.industryApplicationId);

  const effectivePumpChecker = enablePumpChecker && [4, 7].includes(industryApplicationId);

  const getData = useCallback(() => {
    if (enablePumpChecker === null) return;
    if (effectivePumpChecker && wellId !== undefined) {
      dispatch(fetchDetailedDailyDowntime({ wellId: wellId }));
    } else if (!effectivePumpChecker && assetId !== undefined) {
      dispatch(
        fetchDetailedDailyDowntimeTier1({
          assetId,
        }),
      );
    }
  }, [dispatch, wellId, assetId, effectivePumpChecker]);

  const handleEdit = (rowData: DetailedDailyDowntimeItem) => {
    setEditData({
      LostOil: rowData.LostOil,
      LostGas: rowData.LostGas,
      LostWater: rowData.LostGas,
      Date: rowData.IndividualDateLocalString ? new Date(rowData.IndividualDateLocalString) : null,
      EndDate: rowData.IndividualEndDateLocalString ? new Date(rowData.IndividualEndDateLocalString) : null,
      DowntimeCodeCategory: rowData.DowntimeCodeCategory,
      DowntimeCodeGrouping: rowData.DowntimeCodeGrouping,
      Comment: rowData.Comment,
      SystemFaultWord: rowData.SystemFaultWord,
      Id: rowData.Id,
      WellId: rowData.WellId,
      WellTimezone: rowData.WellTimezone,
      Hours: rowData.Hours,
    });
    setAddEditOpen(true);
  };
  const handleDelete = (id: number) => {
    setDeleteId(id);
  };
  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    const resp = await deleteDetailedDailyDowntime(deleteId);
    if (resp) {
      getData();
    }
  };

  useEffect(() => {
    getData();
    dispatch(fetchDetailedDailyCategories());
  }, [getData]);
  useEffect(() => {
    const loadLayout = async () => {
      setIsWidgetLoading(true);
      try {
        await dispatch(fetchColumnsData(`Downtime`));
      } finally {
        setIsWidgetLoading(false);
      }
    };
    loadLayout();
  }, [dispatch]);
  const loading = enablePumpChecker === null || dataLoading || isWidgetLoading;
  return (
    <div className='detailed-daily-table-container'>
      {loading ? (
        <div className='is-loading'>
          <Loader />
        </div>
      ) : (
        <DetailedDailyTable
          data={data}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          onAddClick={() => setAddEditOpen(true)}
        />
      )}
      <Modal
        open={deleteId !== null}
        setOpen={() => setDeleteId(null)}
        message='Are you sure you wish to delete this Well Downtime entry?'
        buttonText='Yes'
        callbackOnConfirm={handleDeleteConfirm}
        title='Delete downtime entry'
        Icon={shutdownIcon}
        buttonColor='#D92D20'
      />
      {addEditOpen && (
        <AddEditDowntime
          wellId={wellId}
          isOpen={addEditOpen}
          editData={editData}
          onClose={() => {
            setAddEditOpen(false);
            setEditData(null);
          }}
          onSaved={() => {
            getData();
          }}
        />
      )}
    </div>
  );
};
export default DetailedDaily;
